import { createStore } from "vuex";
import axios from "axios";
import { darkModeKey } from "@/config.js";
import { getErrorMessage } from "@/utils/error-message";
import { auth } from "./auth.module";
import { orders } from "./orders.module";
import { websites } from "./websites.module";
import { cart } from "./cart.module";
import { products } from "./products.module";
import { invoices } from "./invoices.module";
import { news } from "./news.module";

import { subscriptions } from "./subscription.module";
import { createCartSync } from "./cart-sync";

export default createStore({
  // Keeps the cart in step with the customer's account, so it survives a
  // cleared browser and follows them to another machine. A plugin rather than
  // component code: the cart is edited from several screens and syncing from
  // each would mean every new caller has to remember. See store/cart-sync.js.
  plugins: [createCartSync()],

  state: {
    /* User */
    userName: null,
    userEmail: null,
    userAvatar: null,

    /* fullScreen - fullscreen form layout (e.g. login page) */
    isFullScreen: false,

    /* Aside */
    isAsideMobileExpanded: false,
    isAsideLgActive: false,
    isAsideCollapsed: false,

    /* Dark mode */
    darkMode: false,

    /* Field focus with ctrl+k (to register only once) */
    isFieldFocusRegistered: false,

    /* Sample data (commonly used) */
    clients: [],
    history: [],

    /* Load failures from the generic `fetch` action, keyed by data source name
       (e.g. fetchErrors.clients). `fetch` is a shared loader with no idea which
       screen is waiting on it, so it records the failure here instead of
       inventing UI; the component that dispatched it renders the message and
       offers the retry. A key is removed as soon as that source loads. */
    fetchErrors: {},

    displayText: "",
    displayHeader: "",
    displayType: "",
  },
  mutations: {
    /* A fit-them-all commit */
    basic(state, payload) {
      state[payload.key] = payload.value;
    },

    /* User */
    user(state, payload) {
      if (payload.name) {
        state.userName = payload.name;
      }
      if (payload.email) {
        state.userEmail = payload.email;
      }
      if (payload.avatar) {
        state.userAvatar = payload.avatar;
      }
    },
  },
  actions: {
    // Pass null (or "") to clear the banner.
    setDisplayMessage({ commit }, payload = null) {
      const message = payload && typeof payload === "object" ? payload : {};

      commit("basic", {
        key: "displayText",
        value: message.displayText ?? "",
      });

      commit("basic", {
        key: "displayType",
        value: message.displayType ?? "",
      });

      commit("basic", {
        key: "displayHeader",
        value: message.displayHeader ?? "",
      });
    },

    asideMobileToggle({ commit, state }, payload = null) {
      const isShow = payload !== null ? payload : !state.isAsideMobileExpanded;

      /* The drawer no longer pushes #app aside. It is `fixed` at z-40 with the
         z-30 <overlay> behind it, so it already floats over the page — which is
         what docs/app-shell-responsive.md specifies: "Overlay states never push
         content", body padding 0 below xl.

         What was here: a hardcoded 15rem left margin on #app. The drawer is
         only 15rem wide when expanded — collapsed it renders at 4rem, so a
         collapsed drawer shifted the page 240px while covering 64px, leaving a
         176px dead gap. (Class names deliberately not spelled out: the purge
         step is a plain string scanner and would keep emitting the utility.)

         Rejected: deriving the shift from `isAsideCollapsed`, 4rem vs 15rem.
         It closes the gap but keeps the push, which on a 375px phone moves two
         thirds of the page past the right edge where `.m-clipped` (overflow:
         hidden) puts it out of reach. It would also have needed a logical
         `margin-inline-start` custom property in the style of `.navbar-inline`
         (css/_translate-rtl.css): a physical `ml-*` shifts content to the right
         under dir="rtl" while the drawer slides in from the right too, stacking
         them. Overlaying makes both problems moot.

         `m-clipped` stays: locking background scroll behind an open drawer is
         the overlay pattern, and it is what makes the push unnecessary. */
      document.documentElement.classList[isShow ? "add" : "remove"](
        "m-clipped"
      );

      commit("basic", {
        key: "isAsideMobileExpanded",
        value: isShow,
      });
    },

    asideCollapseToggle({ commit, state }) {
      commit("basic", {
        key: "isAsideCollapsed",
        value: !state.isAsideCollapsed,
      });
    },

    asideLgToggle({ commit, state }, payload = null) {
      commit("basic", {
        key: "isAsideLgActive",
        value: payload !== null ? payload : !state.isAsideLgActive,
      });
    },

    fullScreenToggle({ commit, state }, value) {
      commit("basic", { key: "isFullScreen", value });

      document.documentElement.classList[value ? "add" : "remove"](
        "full-screen"
      );
    },

    darkMode({ commit, state }) {
      const value = !state.darkMode;

      document.documentElement.classList[value ? "add" : "remove"]("dark");

      localStorage.setItem(darkModeKey, value ? "1" : "0");

      commit("basic", {
        key: "darkMode",
        value,
      });
    },

    fetch({ commit, state }, payload) {
      // Record/clear the failure for this data source only, leaving any other
      // source's error untouched.
      const setFetchError = (message) => {
        const next = { ...state.fetchErrors };

        if (message) {
          next[payload] = message;
        } else if (!(payload in next)) {
          return; // nothing to clear
        } else {
          delete next[payload];
        }

        commit("basic", { key: "fetchErrors", value: next });
      };

      setFetchError(null);

      axios
        .get(`data-sources/${payload}.json`)
        .then((r) => {
          if (r.data && r.data.data) {
            commit("basic", {
              key: payload,
              value: r.data.data,
            });
            return;
          }

          // A 200 we can't read is still a failure: nothing gets committed, so
          // the caller would otherwise wait forever on data that never arrives.
          console.error(
            `Data source "${payload}" responded without any data:`,
            r?.data
          );
          setFetchError("We couldn't load this data. Please try again.");
        })
        .catch((error) => {
          console.error(`Failed to load data source "${payload}":`, error);
          setFetchError(
            getErrorMessage(error, "We couldn't load this data. Please try again.")
          );
        });
    },
  },
  modules: {
    auth,
    orders,
    websites,
    cart,
    products,
    invoices,
    news,
    subscriptions
  },
});
